import { Scroll } from '@/lib/scroll'
import { onPageIn, onMount, onDestroy } from '@/modules/_'
import gsap from '@lib/gsap'

interface TeamMemberModalConfig {
  readonly modal: HTMLElement
  isOpen: boolean
}

interface TeamMemberElements {
  readonly cardImage: HTMLImageElement | null
  readonly cardName: HTMLElement | null
  readonly cardRole: HTMLElement | null
  readonly cardBio: HTMLElement | null
  readonly cardEmail: HTMLElement | null
  readonly cardInstagram: HTMLElement | null
  readonly cardLinkedin: HTMLElement | null
}

interface ModalElements {
  readonly modalImage: HTMLImageElement | null
  readonly modalName: HTMLElement | null
  readonly modalBioTitle: HTMLElement | null
  readonly modalRole: HTMLElement | null
  readonly modalBio: HTMLElement | null
  readonly modalEmail: HTMLElement | null
  readonly modalLinkedin: HTMLElement | null
  readonly modalInstagram: HTMLElement | null
}

interface SocialLinkData {
  readonly element: HTMLElement | null
  readonly url: string | null
  readonly hasValidUrl: boolean
}

interface EventCleanup {
  readonly cleanup: () => void
}

type AnimationEasing = 'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
type OpacityValue = '0' | '1'
type ScaleValue = 'scale(0.95)' | 'scale(1)'
type OverflowValue = 'hidden' | ''
type DisplayValue = 'flex' | 'none' | 'block'
type PointerEventsValue = 'auto' | 'none'

const ANIMATION_DURATION: number = 400
const ANIMATION_EASING: AnimationEasing =
  'all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)'

export default function (element: HTMLElement, dataset: DOMStringMap): void {
  let modalConfig: TeamMemberModalConfig | null = null
  let eventCleanupFunctions: Array<() => void> = []

  let modalTimeline: GSAPTimeline | null = null

  function getTeamMemberElements(card: HTMLElement): TeamMemberElements {
    return {
      cardImage: card.querySelector<HTMLImageElement>(
        '[data-team-member-image] img'
      ),
      cardName: card.querySelector<HTMLElement>('[data-team-member-name]'),
      cardRole: card.querySelector<HTMLElement>('[data-team-member-role]'),
      cardBio: card.querySelector<HTMLElement>('[data-team-member-bio]'),
      cardEmail: card.querySelector<HTMLElement>('[data-team-member-email]'),
      cardInstagram: card.querySelector<HTMLElement>(
        '[data-team-member-instagram]'
      ),
      cardLinkedin: card.querySelector<HTMLElement>(
        '[data-team-member-linkedin]'
      ),
    }
  }

  function getModalElements(modalElement: HTMLElement): ModalElements {
    return {
      modalImage: modalElement.querySelector<HTMLImageElement>(
        '[data-active-member="img"] img'
      ),
      modalName: modalElement.querySelector<HTMLElement>(
        '[data-active-member="name"]'
      ),
      modalRole: modalElement.querySelector<HTMLElement>(
        '[data-active-member="role"]'
      ),
      modalBio: modalElement.querySelector<HTMLElement>(
        '[data-active-member="bio"]'
      ),
      modalBioTitle: modalElement.querySelector<HTMLElement>(
        '[data-active-member="bio-title"]'
      ),
      modalEmail: modalElement.querySelector<HTMLElement>(
        '[data-active-member="email"]'
      ),
      modalLinkedin: modalElement.querySelector<HTMLElement>(
        '[data-active-member="linkedin"]'
      ),
      modalInstagram: modalElement.querySelector<HTMLElement>(
        '[data-active-member="instagram"]'
      ),
    }
  }

  function hasValidBioContent(bioText: string | null): boolean {
    if (!bioText) return false
    const trimmedBio: string = bioText.trim()
    return trimmedBio.length > 0 && trimmedBio !== '-' && trimmedBio !== 'N/A'
  }

  function isValidUrl(url: string | null): boolean {
    if (!url) return false
    const trimmedUrl: string = url.trim()
    if (trimmedUrl === '' || trimmedUrl === '#') return false

    try {
      new URL(
        trimmedUrl.startsWith('http') ? trimmedUrl : `https://${trimmedUrl}`
      )
      return true
    } catch {
      return false
    }
  }

  function extractSocialData(element: HTMLElement | null): SocialLinkData {
    if (!element) {
      return {
        element: null,
        url: null,
        hasValidUrl: false,
      }
    }

    // Check for href attribute (if it's a link)
    let url: string | null = element.getAttribute('href')

    // If no href, check for data attributes or text content
    if (!url) {
      url =
        element.getAttribute('data-url') ||
        element.getAttribute('data-link') ||
        element.textContent?.trim() ||
        null
    }

    return {
      element,
      url,
      hasValidUrl: isValidUrl(url),
    }
  }

  function updateModalContent(
    teamElements: TeamMemberElements,
    modalElements: ModalElements
  ): void {
    const {
      cardImage,
      cardName,
      cardRole,
      cardBio,
      cardEmail,
      cardInstagram,
      cardLinkedin,
    } = teamElements

    const {
      modalImage,
      modalName,
      modalRole,
      modalBio,
      modalBioTitle,
      modalEmail,
      modalInstagram,
      modalLinkedin,
    } = modalElements

    // Update basic content
    if (cardImage && modalImage) {
      modalImage.src = cardImage.src
      modalImage.alt = cardImage.alt || ''
    }

    if (cardName && modalName) {
      modalName.textContent = cardName.textContent
    }

    if (cardRole && modalRole) {
      modalRole.textContent = cardRole.textContent
    }

    // Update bio with conditional title display
    const bioText: string | null = cardBio?.textContent || null
    const hasBio: boolean = hasValidBioContent(bioText)
    const dataWrap = document.querySelector('.active_member_data')
    if (modalBio) {
      if (hasBio) {
        modalBio.textContent = bioText
        modalBio.style.display = 'block'
        dataWrap?.setAttribute('data-lenis-prevent', '')
      } else {
        modalBio.style.display = 'none'
        dataWrap?.removeAttribute('data-lenis-prevent')
      }
    }

    // Show/hide bio title based on bio content
    if (modalBioTitle) {
      modalBioTitle.style.display = hasBio ? 'block' : 'none'
    }

    const email = cardEmail?.getAttribute('data-team-member-email') as string

    const linkedin = cardLinkedin?.getAttribute(
      'data-team-member-linkedin'
    ) as string

    if (modalEmail) {
      if (email) {
        modalEmail?.setAttribute('href', email)
        modalEmail.style.display = 'block'
      } else {
        modalEmail.style.display = 'none'
      }
    }

    if (modalLinkedin) {
      if (linkedin) {
        modalLinkedin?.setAttribute('href', linkedin)
        modalLinkedin.style.display = 'block'
      } else {
        modalLinkedin.style.display = 'none'
      }
    }
  }

  function createModalTimeline() {
    const activeMemberContent = document.querySelector('.active_member_content')
    const activeMemberOverlay = document.querySelector('.active_member_overlay')

    activeMemberOverlay?.addEventListener('click', closeTeamMemberModal)

    const tl = gsap.timeline({ paused: true })

    // Animate in sequence
    tl.to('.active_member_wrap', {
      opacity: 1,
      ease: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
      duration: 0,
      pointerEvents: 'auto',
    })
      .to(
        activeMemberOverlay,
        {
          opacity: 1,
          ease: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          duration: 0.4,
        },
        '<'
      )
      .to(activeMemberContent, {
        x: '0%',
        ease: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        duration: 0.6,
      })

    return tl
  }

  function openTeamMemberModal(card: HTMLElement): void {
    const taxiElement = document.querySelector('[data-taxi]') as HTMLElement

    taxiElement.style.transform = 'none'

    if (!modalConfig) return

    const teamElements: TeamMemberElements = getTeamMemberElements(card)
    const modalElements: ModalElements = getModalElements(modalConfig.modal)

    updateModalContent(teamElements, modalElements)

    Scroll.stop()

    if (modalTimeline) {
      modalTimeline.kill()
    }

    modalTimeline = createModalTimeline()

    modalTimeline.play()

    modalConfig.isOpen = true
  }

  function closeTeamMemberModal(): void {
    if (!modalConfig || !modalConfig.isOpen) return
    modalTimeline?.reverse()

    modalTimeline?.eventCallback('onReverseComplete', () => {
      Scroll.start()
    })
    modalConfig.isOpen = false
  }

  function handleCardClick(card: HTMLElement): (event: MouseEvent) => void {
    return function (event: MouseEvent): void {
      event.preventDefault()
      openTeamMemberModal(card)
    }
  }

  function handleModalBackgroundClick(event: MouseEvent): void {
    if (!modalConfig || event.target !== modalConfig.modal) return
    closeTeamMemberModal()
  }

  function handleCloseButtonClick(event: MouseEvent): void {
    event.preventDefault()
    closeTeamMemberModal()
  }

  function handleEscapeKeyPress(event: KeyboardEvent): void {
    if (!modalConfig || event.key !== 'Escape' || !modalConfig.isOpen) return
    event.preventDefault()
    closeTeamMemberModal()
  }

  function initializeModal(): void {
    const closeButton: HTMLElement | null = document.querySelector(
      '.modal-expanded_close_wrap.is--about'
    )

    const teamMemberCards: NodeListOf<HTMLElement> = document.querySelectorAll(
      '[data-card="team-member"][data-wf--team-member--variant="large"]'
    )

    const modal: HTMLElement | null = document.querySelector(
      '[data-team-member="active"]'
    )

    if (!modal || !closeButton) {
      console.warn('Team member modal: Required elements not found')
      return
    }

    modalConfig = {
      modal,
      isOpen: false,
    }

    // Add event listeners and store cleanup functions
    teamMemberCards.forEach((card: HTMLElement): void => {
      const clickHandler = handleCardClick(card)
      card.addEventListener('click', clickHandler)

      eventCleanupFunctions.push((): void => {
        card.removeEventListener('click', clickHandler)
      })
    })

    modal.addEventListener('click', handleModalBackgroundClick)
    closeButton.addEventListener('click', handleCloseButtonClick)
    document.addEventListener('keydown', handleEscapeKeyPress)

    // Store cleanup functions
    eventCleanupFunctions.push(
      (): void =>
        modal.removeEventListener('click', handleModalBackgroundClick),
      (): void =>
        closeButton.removeEventListener('click', handleCloseButtonClick),
      (): void => document.removeEventListener('keydown', handleEscapeKeyPress)
    )
  }

  function cleanup(): void {
    // Clean up all event listeners
    eventCleanupFunctions.forEach((cleanupFn: () => void): void => {
      cleanupFn()
    })
    eventCleanupFunctions = []

    // Close modal if open and restore scrolling
    if (modalConfig && modalConfig.isOpen) {
      enableScrolling()
      modalConfig.isOpen = false
    }

    modalConfig = null
  }

  onMount((): void => {
    initializeModal()
  })

  onPageIn(async (): Promise<void> => {
    // Reinitialize if needed when page comes into view
    if (!modalConfig) {
      initializeModal()
    }
  })

  onDestroy((): void => {
    cleanup()
  })
}
